import { useState, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useGQLMutation } from '../../../../hooks/GQLUtilityHooks'
import { graphql } from '../../../../gql'
import { toastSuccess, toastError } from '../../../../lib/toastHelpers'
import { filterObject } from '../../../../lib/Util'
import { useSerialNumbersModal } from '../../../../components/SerialNumbersModal'
import { ReturnFormData } from '../../components/EditReturnItemModal'

// Mutations
const createProductMovePackMutation = graphql(`
  mutation CreateProductMovePackReturn($type: String!, $order: Int!, $moves: [createProductMove]!) {
    createProductMovePack(type: $type, order: $order, moves: $moves) {
      data {
        id
      }
      errors
    }
  }
`)

const updateOrderProcessStatusMutation = graphql(`
  mutation UpdateOrderProcessStatusForReturns($id: Int!, $status: String!, $note: String) {
    updateOrder(id: $id, processStatusRelation: { status: $status, note: $note }) {
      data {
        id
        processStatus {
          id
          name
        }
      }
      errors
    }
  }
`)

interface UseReturnsProps {
  orderId: number
  orderProcessStatusId: string
  onSuccess?: () => void
}

export const useReturns = ({ orderId, orderProcessStatusId, onSuccess }: UseReturnsProps) => {
  const [activeReturns, setActiveReturns] = useState<ReturnFormData[]>([])
  const queryClient = useQueryClient()
  const { serialNumbersModalProps, open: openSerialNumbers } = useSerialNumbersModal()

  // Mutations
  const { mutate: updateOrderStatus } = useGQLMutation(updateOrderProcessStatusMutation, ['updateOrderProcessStatus'])

  const { mutate: createReturn, isPending: isCreatingReturn } = useGQLMutation(
    createProductMovePackMutation,
    ['createProductMovePackReturn'],
    {
      onSuccess: async () => {
        // Update order status to returned if it's the first return
        if (orderProcessStatusId !== 'returned') {
          updateOrderStatus({
            id: orderId,
            status: 'returned',
            note: 'Naskladnění vratkou',
          })
        }

        setActiveReturns([])
        queryClient.invalidateQueries({ queryKey: ['productMovePacks', { orderId }] })
        queryClient.invalidateQueries({ queryKey: ['order', { id: orderId }] })
        onSuccess?.()
        toastSuccess('Vratka byla úspěšně vytvořena')
      },
    },
  )

  // Add a return to the active returns list
  const addReturn = useCallback(
    async (
      returnData: ReturnFormData,
      orderItem: any,
      existingQuantity: number,
      availableToReturn: number,
    ): Promise<void> => {
      // Check if this would exceed available quantity
      const existingReturnsForItem = activeReturns
        .filter(r => {
          if (r.orderItemId !== returnData.orderItemId) return false
          if (!returnData.productId || !r.productId) return true
          return r.productId === returnData.productId
        })
        .reduce((sum, r) => sum + r.amount, 0)

      const actualAvailable = availableToReturn - existingReturnsForItem

      if (returnData.amount > actualAvailable) {
        toastError(`Maximální množství k vrácení je ${actualAvailable} ks`)
        return Promise.reject(new Error('Exceeded available quantity'))
      }

      // Handle serial numbers if required
      if (orderItem.product?.requiredSerialNumbers && returnData.amount > 0) {
        return new Promise<void>(resolve => {
          openSerialNumbers(parseInt(returnData.amount.toString()), orderItem.product, serialNumbers => {
            returnData.serialNumbers = serialNumbers
            setActiveReturns(prev => [...prev, returnData])
            resolve()
          })
        })
      }

      setActiveReturns(prev => [...prev, returnData])
      return Promise.resolve()
    },
    [activeReturns, openSerialNumbers],
  )

  // Remove a return from the active returns list
  const removeReturn = useCallback((index: number) => {
    setActiveReturns(prev => prev.filter((_, i) => i !== index))
  }, [])

  // Process all active returns
  const processReturns = useCallback(
    (orderItems: any[], productMoves?: any[]) => {
      const movesByDepot: Record<number, any[]> = {}

      activeReturns.forEach(returnItem => {
        const depotId = returnItem.depotId
        if (!movesByDepot[depotId]) {
          movesByDepot[depotId] = []
        }

        // Find the order item and its moves
        const orderItem = orderItems.find(item => item.id === returnItem.orderItemId)
        let supplierId = null
        let purchasePrice = 0

        if (orderItem?.moves && productMoves) {
          const moveWithDepot = orderItem.moves.find((m: any) => m.productDepotFrom?.depot?.id === depotId)
          supplierId = moveWithDepot?.productDepotFrom?.supplier?.id || null
          purchasePrice = typeof moveWithDepot?.purchasePrice === 'number' ? moveWithDepot.purchasePrice : 0
        }

        movesByDepot[depotId].push(
          filterObject({
            product: returnItem.productId,
            depotTo: depotId,
            amount: returnItem.amount,
            batch: returnItem.batch || null,
            expirationDate: returnItem.expirationDate || null,
            position1: returnItem.position1 || null,
            position2: returnItem.position2 || null,
            position3: returnItem.position3 || null,
            note: returnItem.note || null,
            orderItem: returnItem.orderItemId,
            supplier: supplierId,
            purchasePrice: purchasePrice,
            ...(returnItem.serialNumbers && returnItem.serialNumbers.length > 0
              ? { serialNumbers: returnItem.serialNumbers }
              : {}),
          }),
        )
      })

      // Create separate move packs for each depot
      Object.entries(movesByDepot).forEach(([_, moves]) => {
        createReturn({
          type: 'in',
          order: orderId,
          moves,
        })
      })
    },
    [activeReturns, createReturn, orderId],
  )

  // Clear all active returns
  const clearActiveReturns = useCallback(() => {
    setActiveReturns([])
  }, [])

  return {
    activeReturns,
    isCreatingReturn,
    addReturn,
    removeReturn,
    processReturns,
    clearActiveReturns,
    serialNumbersModalProps,
  }
}
